import { useQuery } from '@tanstack/react-query';

import aggregatorV3Abi from 'lib/web3/chainlink/aggregatorV3Abi';
import bscPublicClient from 'lib/web3/chainlink/bscPublicClient';
import { getChainlinkFeedForToken } from 'lib/web3/chainlink/feeds';

export interface ChainlinkTokenPrice {
  readonly price: number;
  readonly pair: string;
  readonly updatedAt: Date;
}

const POLL_INTERVAL = 30_000;

export function chainlinkTokenPriceQueryOptions(tokenAddress: string | undefined) {
  const feed = getChainlinkFeedForToken(tokenAddress);

  return {
    queryKey: [ 'chainlink-token-price', feed?.feedAddress ],
    enabled: Boolean(feed),
    refetchInterval: POLL_INTERVAL,
    staleTime: POLL_INTERVAL,
    retry: false,
    queryFn: async(): Promise<ChainlinkTokenPrice> => {
      if (!feed) {
        throw new Error('No Chainlink feed configured for this token');
      }

      const [ decimals, roundData ] = await bscPublicClient.multicall({
        contracts: [
          { address: feed.feedAddress, abi: aggregatorV3Abi, functionName: 'decimals' },
          { address: feed.feedAddress, abi: aggregatorV3Abi, functionName: 'latestRoundData' },
        ] as const,
        allowFailure: false,
      });

      const [ , answer, , updatedAt ] = roundData;

      return {
        price: Number(answer) / 10 ** decimals,
        pair: feed.pair,
        updatedAt: new Date(Number(updatedAt) * 1000),
      };
    },
  };
}

export default function useChainlinkTokenPrice(tokenAddress: string | undefined) {
  return useQuery(chainlinkTokenPriceQueryOptions(tokenAddress));
}
